import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Clock, Download, Filter, LogOut, Play } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import {
  Field,
  HistoryChip,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  selectCls,
} from "@/components/masters/UI";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  attendanceLabel,
  downloadCsv,
  lateByMinutes,
  toCsv,
} from "@/lib/csv";

const todayStr = () => {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const ReportPage = () => {
  const { isAdmin, isLocationAdmin } = useAuth();

  const [date, setDate] = useState(todayStr());
  const [locationId, setLocationId] = useState("");
  const [shiftId, setShiftId] = useState("");
  const [deptId, setDeptId] = useState("");
  const [q, setQ] = useState("");

  const [lookups, setLookups] = useState({ locations: [], shifts: [], departments: [] });
  const [rows, setRows] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  // Load lookups whenever the location filter changes. Supervisor's location
  // is auto-picked on first fetch (backend only returns their own).
  const loadLookups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (!isLocationAdmin && locationId) {
        params.location_id = locationId;
      }
      const { data } = await api.get("/reports/lookups", { params });
      setLookups(data);
      // Auto-pin single-location callers (Supervisor, Location Admin).
      if (!locationId && data.locations.length === 1) {
        setLocationId(data.locations[0].id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [locationId, isLocationAdmin]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  // Clear child filters when parent changes.
  useEffect(() => {
    setShiftId("");
    setDeptId("");
  }, [locationId]);

  const canRun = !!date && (isLocationAdmin || !!locationId) && !!shiftId;

  const runReport = async () => {
    if (!canRun) return;
    setRunning(true);
    try {
      const params = {
        date,
        shift_id: shiftId,
        ...(deptId ? { department_id: deptId } : {}),
      };
      if (!isLocationAdmin && locationId) {
        params.location_id = locationId;
      }
      const { data } = await api.get("/reports/attendance", { params });
      setRows(data.rows || []);
      setSession(data.session || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      `${r.employee_id} ${r.name || ""} ${r.department_name || ""}`
        .toLowerCase()
        .includes(s)
    );
  }, [q, rows]);

  const kpis = useMemo(() => {
    const acc = { present: 0, late: 0, absent: 0 };
    for (const r of filtered) {
      if (r.status === "present" || r.status === "half_day_leave") acc.present += 1;
      else if (r.status === "absent") acc.absent += 1;
      if (r.is_late) acc.late += 1;
    }
    return acc;
  }, [filtered]);

  const shiftById = useMemo(() => {
    const m = {};
    for (const s of lookups.shifts) m[s.id] = s;
    return m;
  }, [lookups.shifts]);

  const sessionCtx = session
    ? {
        date: session.date,
        shift_start: session.shift_start,
        grace_minutes: session.grace_minutes,
      }
    : null;

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const shift = shiftById[shiftId] || {};
    const csv = toCsv(filtered, [
      { header: "Employee ID", get: (r) => r.employee_id },
      { header: "Name", get: (r) => r.name || "" },
      { header: "Department", get: (r) => r.department_name || "" },
      { header: "Shift", get: (r) => r.shift_name || "" },
      { header: "Status", get: (r) => attendanceLabel(r) },
      { header: "Is Late", get: (r) => (r.status === "absent" ? "" : (r.is_late ? "Yes" : "No")) },
      { header: "Scan-In", get: (r) => r.scan_in_time || "" },
      { header: "Scan-Out", get: (r) => r.scan_out_time || "" },
      {
        header: "Late By",
        get: (r) => {
          const late = lateByMinutes(r, sessionCtx);
          return late != null ? String(late) : "";
        },
      },
    ]);
    downloadCsv(
      `attendance-report-${date}-${(shift.shift_name || "shift").replace(/\s+/g, "_")}`,
      csv
    );
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="report-page">
      <AppHeader />
      <PageShell
        eyebrow="Reports"
        title="Attendance Report"
        subtitle="Filter by Date, Location, Shift and Department. Absent employees are listed automatically."
        testId="report-shell"
      >
        <div
          className="mb-4 rounded-2xl border bg-white p-5 shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
          data-testid="report-filters"
        >
          <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
            <Filter className="h-3.5 w-3.5" />
            Filters
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_1.4fr_1.4fr_1.4fr_auto]">
            <Field label="Date">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="report-date"
                className={selectCls}
              />
            </Field>
            <Field label="Location">
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={!isAdmin && lookups.locations.length <= 1}
                data-testid="report-location"
                className={selectCls}
              >
                <option value="">Select…</option>
                {lookups.locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Shift">
              <select
                value={shiftId}
                onChange={(e) => setShiftId(e.target.value)}
                disabled={!isLocationAdmin && !locationId}
                data-testid="report-shift"
                className={selectCls}
              >
                <option value="">Select…</option>
                {lookups.shifts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.shift_name} · {s.shift_start}-{s.shift_end}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Department (optional)">
              <select
                value={deptId}
                onChange={(e) => setDeptId(e.target.value)}
                disabled={!locationId}
                data-testid="report-department"
                className={selectCls}
              >
                <option value="">All departments</option>
                {lookups.departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex items-end gap-2">
              <PrimaryButton
                testId="report-run"
                onClick={runReport}
                disabled={!canRun || running || loading}
              >
                <Play className="mr-1 inline h-4 w-4" />
                {running ? "Running…" : "Run"}
              </PrimaryButton>
              <OutlineButton
                testId="report-export"
                onClick={exportCsv}
                disabled={filtered.length === 0}
              >
                <Download className="mr-1 inline h-4 w-4" />
                Export
              </OutlineButton>
            </div>
          </div>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-4">
          <KpiCard testId="report-kpi-total" label="Total" value={filtered.length} tone="slate" />
          <KpiCard testId="report-kpi-present" label="Present" value={kpis.present} tone="success" />
          <KpiCard testId="report-kpi-late" label="Late" value={kpis.late} tone="error" />
          <KpiCard testId="report-kpi-absent" label="Absent" value={kpis.absent} tone="navy" />
        </div>

        <div className="mb-3 flex items-center justify-between gap-3">
          <SearchBox
            value={q}
            onChange={setQ}
            placeholder="Search employee, name or department…"
            testId="report-search"
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-vision-slate/60">
            {rows.length === 0 ? "Run the report to view rows" : `Showing ${filtered.length} of ${rows.length}`}
          </span>
        </div>

        {rows.length === 0 ? (
          <div
            className="rounded-2xl border bg-white p-10 text-center text-sm text-vision-slate/70 shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="report-empty"
          >
            Choose Date + Location + Shift and hit <b>Run</b>.
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-2xl border bg-white shadow-card"
            style={{ borderColor: "rgba(52,73,94,0.08)" }}
            data-testid="report-table"
          >
            <div
              className="hidden grid-cols-[1.4fr_1fr_1.2fr_1fr_120px_100px_100px_110px] gap-4 border-b bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid"
              style={{ borderColor: "rgba(52,73,94,0.08)" }}
            >
              <div>Employee</div>
              <div>Employee ID</div>
              <div>Department</div>
              <div>Shift</div>
              <div>Status</div>
              <div>Scan-In</div>
              <div>Scan-Out</div>
              <div>Late By</div>
            </div>
            {filtered.map((r) => (
              <ReportRow key={r.employee_id} r={r} sessionCtx={sessionCtx} />
            ))}
          </div>
        )}
      </PageShell>
    </div>
  );
};

const ReportRow = ({ r, sessionCtx }) => {
  const label = attendanceLabel(r);
  const late = lateByMinutes(r, sessionCtx);
  const tone =
    r.status === "present"
      ? r.out_ts
        ? "navy"
        : "success"
      : "slate";
  return (
    <div
      className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1.4fr_1fr_1.2fr_1fr_120px_100px_100px_110px] md:gap-4"
      style={{ borderColor: "rgba(52,73,94,0.06)" }}
      data-testid={`report-row-${r.employee_id}`}
    >
      <div className="min-w-0">
        <div className="truncate font-display text-sm font-bold text-vision-ink">{r.name}</div>
      </div>
      <div className="text-sm text-vision-slate">{r.employee_id}</div>
      <div className="text-sm text-vision-slate">{r.department_name || "—"}</div>
      <div className="text-sm text-vision-slate">{r.shift_name}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <HistoryChip tone={tone} label={label} testId={`report-row-${r.employee_id}-status`} />
        {r.is_late && (
          <span
            className="chip text-[11px] font-semibold"
            style={{
              backgroundColor: "rgba(231, 76, 60, 0.12)",
              color: "var(--vision-error)",
              border: "1px solid rgba(231, 76, 60, 0.35)",
            }}
            data-testid={`report-row-${r.employee_id}-late-chip`}
          >
            Late
          </span>
        )}
      </div>
      <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">
        {r.scan_in_time ? (
          <>
            <Clock className="mr-1 inline h-3.5 w-3.5 text-vision-slate/60" />
            {r.scan_in_time}
          </>
        ) : (
          <span className="text-vision-slate/40">—</span>
        )}
      </div>
      <div
        className="font-display text-sm font-bold numeric-tabular"
        style={{ color: r.scan_out_time ? "var(--vision-navy)" : "rgba(52,73,94,0.35)" }}
      >
        {r.scan_out_time ? (
          <>
            <LogOut className="mr-1 inline h-3.5 w-3.5" />
            {r.scan_out_time}
          </>
        ) : (
          <span>—</span>
        )}
      </div>
      <div
        className="text-xs font-semibold numeric-tabular"
        data-testid={`report-row-${r.employee_id}-late`}
        style={{ color: late != null ? "var(--vision-error)" : "rgba(52,73,94,0.35)" }}
      >
        {late != null ? `Late by ${late} min` : "—"}
      </div>
    </div>
  );
};

const kpiTone = {
  success: { bg: "rgba(39,174,96,0.14)", color: "#1e8a4a" },
  orange:  { bg: "rgba(247,144,9,0.14)", color: "var(--vision-orange)" },
  navy:    { bg: "rgba(0,53,158,0.10)",  color: "var(--vision-navy)" },
  slate:   { bg: "rgba(52,73,94,0.08)",  color: "var(--vision-slate)" },
  error:   { bg: "rgba(231,76,60,0.14)",  color: "var(--vision-error)" },
};

const KpiCard = ({ testId, label, value, tone }) => (
  <div
    className="rounded-2xl border bg-white p-4 shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60">
        {label}
      </div>
      <span
        className="chip"
        style={{ backgroundColor: kpiTone[tone].bg, color: kpiTone[tone].color, border: "none" }}
      >
        <CalendarDays className="h-3 w-3" />
      </span>
    </div>
    <div className="mt-2 font-display text-3xl font-extrabold text-vision-ink numeric-tabular">
      {value.toLocaleString()}
    </div>
  </div>
);

export default ReportPage;
